/*
 * Copyright (c) 2024. Dave van Rijn Development
 */

import {UserStatus, UserType} from '../util/enums';
import {BaseFields, BaseModel, BaseProps, FieldDefinition, ModelFieldType} from './baseModel';
import {ModelResponse} from '../util/interfaces';
import {getUser, login, register, updateUser} from '../util/requestSender';
import {ResponseData} from '@dvrd/fetch';
import {deepClone} from '../util/utils';
import {generateUUID} from '@dvrd/dvr-controls';

interface Props extends BaseProps {
    email?: string;
    firstname?: string;
    lastname?: string;
    status?: UserStatus;
    flex?: Record<string, any>;
    last_login?: string;
    two_factor_enabled?: boolean;
    user_type?: UserType;
}

export interface UserFields extends BaseFields {
    email: string;
    firstname: string;
    lastname: string;
    status: UserStatus;
    flex: Record<string, any>;
    last_login: string;
    two_factor_enabled: boolean;
    user_type: UserType;
}

const fields: UserFields = {
    id: '',
    created_at: '',
    updated_at: '',
    email: '',
    firstname: '',
    lastname: '',
    status: UserStatus.ACTIVE,
    flex: {},
    last_login: '',
    two_factor_enabled: true,
    user_type: UserType.USER
};

type FieldDefType = keyof UserFields;

const fieldsDefinition: Record<FieldDefType, FieldDefinition> = {
    id: new FieldDefinition(ModelFieldType.STRING),
    created_at: new FieldDefinition(ModelFieldType.STRING),
    updated_at: new FieldDefinition(ModelFieldType.STRING, {allowNull: true}),
    email: new FieldDefinition(ModelFieldType.STRING),
    firstname: new FieldDefinition(ModelFieldType.STRING),
    lastname: new FieldDefinition(ModelFieldType.STRING),
    last_login: new FieldDefinition(ModelFieldType.STRING),
    two_factor_enabled: new FieldDefinition(ModelFieldType.BOOLEAN),
    user_type: new FieldDefinition(ModelFieldType.STRING, {defaultValue: UserType.USER}),
    status: new FieldDefinition(ModelFieldType.STRING, {defaultValue: UserStatus.ACTIVE}),
    flex: new FieldDefinition(ModelFieldType.OBJECT)
};

const ignoreProps: Array<keyof UserFields> = [];

export default class User extends BaseModel<Props, UserFields, FieldDefType> {
    static get = (callback: ModelResponse<User | null>) => {
        getUser({
            callback: (data: ResponseData) => {
                const user = data.success ? new User(data.user) : null;
                callback(user, data.success, data);
            }
        });
    };

    static login = (email: string, password: string, callback: ModelResponse<User | null>) => {
        login({
            data: {email, password}, callback: (data: ResponseData) => {
                let user: User | null = null;
                if (data.success && !data.require_two_factor)
                    user = new User(data.user);
                callback(user, data.success, data);
            }
        });
    };

    constructor(props?: Props) {
        super(ignoreProps, fieldsDefinition, fields, props);
        this.build(props);
        this.identity = props?.identity ?? `User.${props?.id ?? generateUUID()}`;
    }

    get email(): string {
        return this.fields.email;
    }

    set email(email: string) {
        this.fields.email = email;
    }

    get firstname(): string {
        return this.fields.firstname;
    }

    set firstname(firstname: string) {
        this.fields.firstname = firstname;
    }

    get lastname(): string {
        return this.fields.lastname;
    }

    set lastname(lastname: string) {
        this.fields.lastname = lastname;
    }

    get fullName(): string {
        let fullName = this.firstname;
        if (this.lastname.length) fullName += ` ${this.lastname}`;
        return fullName;
    }

    get status(): UserStatus {
        return this.fields.status;
    }

    set status(status: UserStatus) {
        this.fields.status = status;
    }

    get flex(): Record<string, any> {
        return this.fields.flex;
    }

    set flex(flex: Record<string, any>) {
        this.fields.flex = flex;
    }

    get lastLogin(): string {
        return this.fields.last_login;
    }

    set lastLogin(last_login: string) {
        this.fields.last_login = last_login;
    }

    get twoFactorEnabled(): boolean {
        return this.fields.two_factor_enabled;
    }

    set twoFactorEnabled(two_factor_enabled: boolean) {
        this.fields.two_factor_enabled = two_factor_enabled;
    }

    get userType(): UserType {
        return this.fields.user_type;
    }

    getFlex = (key: string): any => {
        return this.flex[key];
    };

    setFlex = (key: string, value: any) => {
        this.flex = {...this.flex, [key]: value};
    };

    clone = (identityMap?: Map<string, any>): User => {
        const props = deepClone(this.fields, identityMap);
        return new User({...props, identity: this.identity});
    };

    register = (password: string, callback: ModelResponse<User>) => {
        register({
            data: {
                email: this.email,
                password,
                flex: this.flex,
                firstname: this.firstname,
                lastname: this.lastname
            }, callback: (data: ResponseData) => {
                if (data.success) this.build(data.user);
                callback(this, data.success, data);
            }
        });
    };

    update = (callback: ModelResponse<User>) => {
        updateUser({
            data: {
                email: this.email, firstname: this.firstname, lastname: this.lastname, flex: this.flex
            }, callback: (data: ResponseData) => {
                if (data.success) this.build(data.user);
                callback(this, data.success, data);
            }
        });
    };

    protected build = (props?: Props) => {
        this.constructFields(props);
        if (props) {
        }
    };
}